import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, BarChart3, CreditCard, FileText, Landmark, Receipt, TrendingUp, Wallet } from "lucide-react";
import { ClientRegister } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { humanise } from "../register/registerLogic";
import { formatSchoolDate } from "../students/studentDisplay";
import { academicSetupService } from "../../services/academicSetupService";
import { financialService, type AuditApprovalEvent, type StudentOption } from "../../services/financialService";
import { todayIso } from "../../utils/dates";
import {
  AgeingSummary,
  ReportCard,
  SheetTable,
  SupplierStatementModal,
  ageingColumns,
  auditColumns,
  studentsOwingColumns,
  type Party,
} from "./ManagementReportsView";
import { auditEventConfig, documentName, payablesAgeingConfig, studentsOwingConfig } from "./managementReports";
import { AsAtBar, PeriodBar } from "./ReportDateBar";
import { quickDays, quickPeriods } from "./reportPeriods";

const ugx = (value: string | number | null | undefined) => `UGX ${Number(value || 0).toLocaleString()}`;
const day = (value: string | null | undefined) => formatSchoolDate(value) || "—";

export interface OverviewAction {
  label: string;
  count: number;
  detail: string;
  link: string;
  tone: string;
}

export interface OverviewService {
  label: string;
  value: number;
  count: number;
  link: string;
}

type Worklist = "owing" | "bills" | "activity";

function Kpi({ label, value, note, icon, to, tone }: { label: string; value: ReactNode; note: string; icon: ReactNode; to: string; tone: string }) {
  return (
    <Link to={to} className="card group transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
      <div className="flex items-start gap-4 px-5 py-4">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-0.5 truncate text-xl font-bold tabular-nums text-gray-900">{value}</p>
          <p className="mt-0.5 text-xs text-gray-400">{note}</p>
        </div>
        <ArrowRight className="mt-1 h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
      </div>
    </Link>
  );
}

/**
 * The finance landing page: where the money stands, what needs doing, and
 * worklists to act on, each searchable, filtered, paged and exportable. The
 * period and "as at" day are shared with Management Reports.
 */
export default function FinanceOverview({
  reportStart,
  reportEnd,
  asOfDate,
  onPeriodChange,
  onAsOfChange,
  canExport,
  canViewAuditLogs,
  actionItems,
  services,
  unreconciledLines,
  suggestedMatches,
  grantBalance,
  activeGrants,
  latestNetPay,
  onOpenStudent,
}: {
  reportStart: string;
  reportEnd: string;
  asOfDate: string;
  onPeriodChange: (start: string, end: string) => void;
  onAsOfChange: (day: string) => void;
  canExport: boolean;
  canViewAuditLogs: boolean;
  actionItems: OverviewAction[];
  services: OverviewService[];
  unreconciledLines: number;
  suggestedMatches: number;
  grantBalance: number;
  activeGrants: number;
  latestNetPay: string | number | undefined;
  onOpenStudent: (student: StudentOption) => void;
}) {
  const today = todayIso();
  const terms = useQuery({ queryKey: ["financial-academic-terms"], queryFn: academicSetupService.getTerms });
  const stats = useQuery({ queryKey: ["financial-stats"], queryFn: financialService.getStats });
  const cashFlow = useQuery({
    queryKey: ["cash-flow-report", reportStart, reportEnd],
    queryFn: () => financialService.getCashFlowReport({ start_date: reportStart, end_date: reportEnd }),
  });
  const receivables = useQuery({ queryKey: ["receivables-ageing", asOfDate], queryFn: () => financialService.getReceivablesAgeing({ as_of: asOfDate }) });
  const payables = useQuery({ queryKey: ["payables-ageing", asOfDate], queryFn: () => financialService.getPayablesAgeing({ as_of: asOfDate }) });
  const outstanding = useQuery({ queryKey: ["outstanding-fees-report"], queryFn: financialService.getOutstandingFeesReport });
  const activity = useQuery({
    queryKey: ["audit-approval-report", reportStart, reportEnd],
    queryFn: () => financialService.getAuditApprovalReport({ start_date: reportStart, end_date: reportEnd }),
    enabled: canViewAuditLogs,
  });

  const [worklist, setWorklist] = useState<Worklist>("owing");
  const [supplier, setSupplier] = useState<Party | null>(null);
  const [event, setEvent] = useState<AuditApprovalEvent | null>(null);

  const methods = stats.data?.payment_methods ?? [];
  const methodPeak = Math.max(...methods.map((method) => Number(method.total_amount)), 1);
  const net = Number(cashFlow.data?.net_cash_movement ?? 0);
  const worklists: { key: Worklist; label: string; count?: number }[] = [
    { key: "owing", label: "Students owing", count: outstanding.data?.by_student.length },
    { key: "bills", label: "Supplier bills due", count: payables.data?.detail.length },
    ...(canViewAuditLogs ? [{ key: "activity" as const, label: "Approval activity", count: activity.data?.events.length }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Title, the reports it leads to, and the dates everything below uses. */}
      <section className="card">
        <div className="card-titlebar">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <BarChart3 className="h-5 w-5 text-primary-600" />
              Finance overview
            </h2>
            <p className="mt-1 text-sm text-gray-500">Where the money stands, what needs attention, and the lists to work through.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/financial/statements" className="btn btn-outline">
              Management reports <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link to="/financial/financial-statements" className="btn btn-outline">
              Financial statements <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="px-6 py-4">
          <div className="filter-panel">
            <PeriodBar
              start={reportStart}
              end={reportEnd}
              onChange={onPeriodChange}
              periods={quickPeriods(today, terms.data ?? [])}
              hint="Cash movement and approval activity cover this period. The same dates carry over to Management Reports."
            />
            <AsAtBar value={asOfDate} onChange={onAsOfChange} days={quickDays(today, terms.data ?? [])} hint="Receivables and supplier bills are aged as at this day." />
          </div>
        </div>
      </section>

      {/* Headline figures; each opens where it is worked on. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Fee income" value={ugx(stats.data?.total_revenue)} note="All receipts recorded" icon={<Wallet className="h-5 w-5" />} to="/financial/payments" tone="bg-primary-50 text-primary-700" />
        <Kpi label="Outstanding fees" value={ugx(stats.data?.outstanding_balance)} note="Still owed on invoices" icon={<CreditCard className="h-5 w-5" />} to="/financial/statements?view=arrears" tone="bg-amber-50 text-amber-700" />
        <Kpi
          label="Overdue invoices"
          value={(stats.data?.overdue_invoices ?? 0).toLocaleString()}
          note={`${ugx(stats.data?.overdue_amount)} past due`}
          icon={<FileText className="h-5 w-5" />}
          to="/financial/invoices"
          tone="bg-red-50 text-red-700"
        />
        <Kpi
          label="Collection rate"
          value={`${Number(stats.data?.collection_rate ?? 0).toFixed(1)}%`}
          note="Collected of all billed"
          icon={<TrendingUp className="h-5 w-5" />}
          to="/financial/statements?view=income"
          tone="bg-emerald-50 text-emerald-700"
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-5">
        <ReportCard
          className="xl:col-span-3"
          title="Cash movement"
          description={`${day(reportStart)} – ${day(reportEnd)}`}
          aside={<Link to="/financial/statements?view=income" className="text-sm font-medium text-primary-700 hover:underline">Cash-flow report</Link>}
        >
          <div className="space-y-4">
            <dl className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-emerald-50 px-4 py-3">
                <dt className="text-xs font-medium text-emerald-700">Money in</dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-emerald-900">{ugx(cashFlow.data?.total_receipts)}</dd>
              </div>
              <div className="rounded-lg bg-red-50 px-4 py-3">
                <dt className="text-xs font-medium text-red-700">Money out</dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-red-900">{ugx(cashFlow.data?.total_payments)}</dd>
              </div>
              <div className="rounded-lg bg-primary-50 px-4 py-3">
                <dt className="text-xs font-medium text-primary-700">Net {net < 0 ? "outflow" : "inflow"}</dt>
                <dd className={`mt-0.5 text-lg font-semibold tabular-nums ${net < 0 ? "text-red-800" : "text-primary-900"}`}>{ugx(Math.abs(net))}</dd>
              </div>
            </dl>
            <div className="grid gap-4 md:grid-cols-2">
              <SheetTable
                rows={cashFlow.data?.receipts}
                rowKey={(row) => row.source}
                empty="No money came in this period."
                columns={[
                  { header: "Money in from", cell: (row) => row.source },
                  { header: "UGX", cell: (row) => Number(row.amount).toLocaleString(), align: "right" },
                ]}
              />
              <SheetTable
                rows={cashFlow.data?.payments}
                rowKey={(row) => row.source}
                empty="No money went out this period."
                columns={[
                  { header: "Money out to", cell: (row) => row.source },
                  { header: "UGX", cell: (row) => Number(row.amount).toLocaleString(), align: "right" },
                ]}
              />
            </div>
          </div>
        </ReportCard>

        <ReportCard className="xl:col-span-2" title="Needs attention" description="Work waiting in finance, largest first." aside={<AlertTriangle className="h-5 w-5 text-amber-500" />}>
          {actionItems.length ? (
            <ul className="space-y-2">
              {[...actionItems].sort((a, b) => b.count - a.count).map((item) => (
                <li key={item.label}>
                  <Link to={item.link} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition hover:shadow-sm ${item.tone}`}>
                    <span>
                      <span className="block text-sm font-semibold">{item.label}</span>
                      <span className="block text-xs opacity-80">{item.detail}</span>
                    </span>
                    <span className="flex items-center gap-2 text-lg font-bold tabular-nums">
                      {item.count}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">Nothing is waiting. Well done.</p>
          )}
        </ReportCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <AgeingSummary
            title="Receivables ageing"
            description={`Unpaid fee invoices as at ${day(asOfDate)}, by how long each is overdue (UGX).`}
            data={receivables.data}
            loading={receivables.isLoading}
            aside={<Link to="/financial/statements?view=ageing" className="text-sm font-medium text-primary-700 hover:underline">Full ageing</Link>}
          />
        </div>
        <ReportCard
          className="xl:col-span-2"
          title="How fees are paid"
          description="Every receipt so far, by payment method."
          aside={<Receipt className="h-5 w-5 text-primary-600" />}
        >
          {methods.length ? (
            <ul className="space-y-3">
              {methods.map((method) => (
                <li key={method.payment_method}>
                  <div className="mb-1 flex justify-between gap-3 text-sm">
                    <span className="text-gray-700">{humanise(method.payment_method)}</span>
                    <span className="font-medium tabular-nums text-gray-900">{ugx(method.total_amount)}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.round((Number(method.total_amount) / methodPeak) * 100)}%` }} />
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">{method.count} payment{method.count === 1 ? "" : "s"}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-sm text-gray-400">No receipts recorded yet.</p>
          )}
        </ReportCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <AgeingSummary
            title="Supplier bills"
            description={`Approved bills unpaid as at ${day(asOfDate)}, by how long each is overdue (UGX).`}
            data={payables.data}
            loading={payables.isLoading}
            aside={<Link to="/financial/payables" className="text-sm font-medium text-primary-700 hover:underline">Payables</Link>}
          />
        </div>
        <div className="grid gap-5 sm:grid-cols-2 xl:col-span-2">
          <ReportCard title="Bank matching" description="Bank lines still to match to the books." aside={<Landmark className="h-5 w-5 text-primary-600" />}>
            <dl className="space-y-3">
              <div className="flex items-baseline justify-between">
                <dt className="text-sm text-gray-600">Unmatched bank lines</dt>
                <dd className={`text-2xl font-bold tabular-nums ${unreconciledLines ? "text-amber-700" : "text-gray-900"}`}>{unreconciledLines}</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-sm text-gray-600">Matches suggested</dt>
                <dd className="text-2xl font-bold tabular-nums text-gray-900">{suggestedMatches}</dd>
              </div>
            </dl>
            <Link to="/financial/banking" className="mt-4 inline-flex items-center text-sm font-medium text-primary-700 hover:underline">
              Reconcile <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </ReportCard>
          <ReportCard title="Grants and payroll" description="Restricted money held, and the last pay run.">
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Unspent grant money</dt>
                <dd className="font-semibold tabular-nums text-gray-900">{ugx(grantBalance)}</dd>
                <dd className="text-xs text-gray-500">{activeGrants} active grant{activeGrants === 1 ? "" : "s"}</dd>
              </div>
              <div className="border-t border-gray-100 pt-3">
                <dt className="text-xs text-gray-500">Latest payroll net pay</dt>
                <dd className="font-semibold tabular-nums text-gray-900">{latestNetPay ? ugx(latestNetPay) : "No pay run yet"}</dd>
              </div>
            </dl>
            <div className="mt-4 flex gap-4 text-sm font-medium">
              <Link to="/financial/grants" className="text-primary-700 hover:underline">Grants</Link>
              <Link to="/financial/payroll" className="text-primary-700 hover:underline">Payroll</Link>
            </div>
          </ReportCard>
        </div>
      </div>

      <section aria-labelledby="services-heading" className="space-y-3">
        <div>
          <h2 id="services-heading" className="text-lg font-semibold text-gray-900">Service charges</h2>
          <p className="text-sm text-gray-500">Transport, boarding, meals and uniforms billed so far.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {services.map((service) => (
            <Link key={service.label} to={service.link} className="card group transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between gap-3 px-5 py-4">
                <div>
                  <p className="text-sm text-gray-500">{service.label}</p>
                  <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{ugx(service.value)}</p>
                  <p className="text-xs text-gray-500">
                    {service.count} record{service.count === 1 ? "" : "s"}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-600" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* The lists to work through: searchable, filtered, paged and exportable. */}
      <section aria-labelledby="worklists-heading" className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="worklists-heading" className="text-lg font-semibold text-gray-900">Worklists</h2>
            <p className="text-sm text-gray-500">Search, filter and page through them; click a row to open it.</p>
          </div>
          <div role="group" aria-label="Worklist" className="segmented">
            {worklists.map((entry) => (
              <button key={entry.key} type="button" aria-pressed={worklist === entry.key} onClick={() => setWorklist(entry.key)} className="segmented-option px-3.5 py-1.5 text-sm">
                {entry.label}
                {entry.count !== undefined && <span className="ml-1.5 tabular-nums opacity-70">{entry.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {worklist === "owing" && (
          <ClientRegister
            title="Students with outstanding fees"
            description="Every learner with an unpaid balance. Click a row to open the account."
            rows={outstanding.data?.by_student}
            config={studentsOwingConfig}
            columns={studentsOwingColumns}
            rowKey={(row) => row.student_pk}
            onOpen={(row) => onOpenStudent({ id: row.student_pk, full_name: row.student_name, student_id: row.student_code, class_name: row.class_name })}
            openLabel={() => "Account"}
            noun={["student", "students"]}
            exportName={{ file: "students-with-outstanding-fees", title: "Students with outstanding fees" }}
            isLoading={outstanding.isLoading}
            isError={outstanding.isError}
            onRetry={() => void outstanding.refetch()}
            emptyText="No student owes fees."
            canExport={canExport}
          />
        )}
        {worklist === "bills" && (
          <ClientRegister
            title="Supplier bills due"
            description={`Approved bills unpaid as at ${day(asOfDate)}, most overdue first. Click a row for the supplier's statement.`}
            rows={payables.data?.detail}
            config={payablesAgeingConfig}
            columns={ageingColumns("Supplier", "Bill")}
            rowKey={(row) => row.invoice_id}
            onOpen={(row) => setSupplier({ id: row.student_id, name: row.student_name })}
            openLabel={() => "Statement"}
            noun={["bill", "bills"]}
            exportName={{ file: `supplier-bills-due-${asOfDate}`, title: `Supplier bills due as at ${asOfDate}` }}
            isLoading={payables.isLoading}
            isError={payables.isError}
            onRetry={() => void payables.refetch()}
            emptyText="No approved supplier bill is unpaid."
            canExport={canExport}
          />
        )}
        {worklist === "activity" && canViewAuditLogs && (
          <ClientRegister
            title="Approval activity"
            description={`Submissions, approvals, returns and reversals, ${day(reportStart)} – ${day(reportEnd)}. Click a row for the full comment.`}
            rows={activity.data?.events}
            config={auditEventConfig}
            columns={auditColumns}
            rowKey={(row) => row.id}
            onOpen={setEvent}
            noun={["action", "actions"]}
            exportName={{ file: `approval-activity-${reportStart}-to-${reportEnd}`, title: `Approval activity, ${reportStart} to ${reportEnd}` }}
            isLoading={activity.isLoading}
            isError={activity.isError}
            onRetry={() => void activity.refetch()}
            emptyText="No approval activity in this period."
            canExport={canExport}
          />
        )}
      </section>

      {supplier && <SupplierStatementModal supplier={supplier} asOf={asOfDate} canExport={canExport} onClose={() => setSupplier(null)} />}
      {event && (
        <DetailsModal
          title={`${documentName(event.document_type)} #${event.object_id}`}
          headline={{ label: "Action", value: event.action_display, note: new Date(event.created_at).toLocaleString() }}
          facts={[
            ["Document", documentName(event.document_type)],
            ["Number", `#${event.object_id}`],
            ["By", event.actor_name],
            ["From status", event.from_status ? humanise(event.from_status) : ""],
            ["To status", event.to_status ? humanise(event.to_status) : ""],
          ]}
          sections={[{ title: "Comment", body: event.comment }]}
          onClose={() => setEvent(null)}
        />
      )}
    </div>
  );
}
